import { Box, Stack, SxProps, Theme, useTheme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { memo } from 'react';
import { bloxValues } from 'themes/constants';

/**
 * Common generic layout styles wrapper s
 * can be reused to wrap different inputs/results
 * components for Search Modal screens.
 */

/**
 * --------------------------------------------------------
 * Results View Layout - inputs + results areas
 * --------------------------------------------------------
 */
type SearchModalBodyLayoutProps = {
  inputsComponent: React.ReactNode;
  resultsComponent: React.ReactNode;
};

export const SearchModalBodyLayout = memo(
  ({ inputsComponent, resultsComponent }: SearchModalBodyLayoutProps) => {
    return (
      <Box
        data-testid="search-modal-layout-container"
        sx={{
          container: 'view / inline-size',
          display: 'grid',
          gridTemplateAreas: `'inputs''results'`,
          gridTemplateRows: 'auto 1fr',
          gridTemplateColumns: '1fr',
          overflow: 'hidden',
          height: '100%',
          width: '100%',
        }}
      >
        <Box
          data-testid="search-modal-layout-inputs"
          sx={{
            gridArea: 'inputs',
            container: 'inputs / inline-size',
            overflow: 'auto',
            fontSize: bloxValues.smallFontSizeRem,
          }}
        >
          {inputsComponent}
        </Box>
        <Box
          data-testid="search-modal-layout-results"
          sx={{
            // Grid container
            gridArea: 'results',
            container: 'results / inline-size',
            overflow: 'auto',
            height: '100%',
            width: '100%',
            minHeight: '100%',
            minWidth: '100%',
            // Typography
            fontSize: bloxValues.smallFontSizeRem,
            lineHeight: 'normal',
            whiteSpace: 'normal',
            overflowWrap: 'break-word',
            wordBreak: 'break-all',
            textWrap: 'nowrap',
            hyphens: 'none',
          }}
        >
          {resultsComponent}
        </Box>
      </Box>
    );
  },
);
SearchModalBodyLayout.displayName = 'SearchModalBodyLayout';

/**
 * --------------------------------------------------------
 * Inputs Bar Layout
 * - Left component area (view switch, input bar, etc)
 * - Right component area (results count, card sort etc)
 * - Bottom component area (applied filters row, etc)
 * --------------------------------------------------------
 */

type SearchModalInputsLayoutProps = {
  leftComponent?: React.ReactNode;
  rightComponent?: React.ReactNode;
  bottomComponent?: React.ReactNode;
};

export const SearchModalInputsLayout = memo(
  ({ leftComponent, rightComponent, bottomComponent }: SearchModalInputsLayoutProps) => {
    const theme = useTheme();

    return (
      <Box
        data-testid="search-modal-inputs-grid"
        sx={{
          container: 'inputgrid / inline-size',
          display: 'grid',
          gridTemplateAreas: `'inputgridtop''inputgridbottom'`,
          gridTemplateRows: 'auto auto',
          gridTemplateColumns: '1fr',
          overflow: 'hidden',
          width: '100%',
          height: 'auto',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {/* Top Bar Area  */}
        <Box
          data-testid="search-modal-inputs-grid-top"
          sx={{
            gridArea: 'inputgridtop',
            container: 'inputgridtop / inline-size',
            overflow: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 0.5,
            pt: 1,
            pb: 0.5,
            pr: 0.5,
            pl: 0.5,
          }}
        >
          {/*  left side - view select, text input... */}
          <Stack
            data-testid="search-modal-inputs-top-left"
            direction="row"
            sx={{
              alignItems: 'center',
              gap: 0.5,
              flexGrow: 1,
            }}
          >
            {leftComponent}
          </Stack>
          {/* right side - results, card sort... */}
          <Stack
            data-testid="search-modal-inputs-top-right"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            {rightComponent}
          </Stack>
        </Box>
        {/* Bottom bar - active filters row */}
        <Box
          data-testid="search-modal-inputs-grid-bottom"
          sx={{
            gridArea: 'inputgridbottom',
            container: 'inputgridbottom / inline-size',
            display: 'flex',
            flexDirection: 'row',
            overflow: 'auto', // scroll on mobile
          }}
        >
          {bottomComponent}
        </Box>
      </Box>
    );
  },
);
SearchModalInputsLayout.displayName = 'SearchModalInputsLayout';

/**
 * --------------------------------------------------------
 * Card View Layout
 * - Grid layout for card view results
 * - Cards can be map <Fragment> of cards + loader jsx
 * --------------------------------------------------------
 */

type SearchModalCardViewLayoutProps = {
  cardsComponent: React.ReactNode;
};

export const SearchModalCardViewLayout = memo(
  ({ cardsComponent }: SearchModalCardViewLayoutProps) => {
    return (
      <Box
        data-testid="search-modal-card-view-layout"
        sx={{
          display: 'grid',
          gap: 2,
          p: 1,
          justifyContent: 'flex-start',
          gridTemplateColumns: 'repeat(auto-fill, minmax(345px, 1fr))',
        }}
      >
        {cardsComponent}
      </Box>
    );
  },
);
SearchModalCardViewLayout.displayName = 'SearchModalCardViewLayout';

/**
 * --------------------------------------------------------
 * Common SX Styles
 * - Reusable style objects for Search Modal layouts
 * --------------------------------------------------------
 */

export const viewSwitchContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexWrap: 'nowrap',
  alignItems: 'center',
  gap: '2px',
};

export const inputsRightContainerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

export const getTopbarResultsTextSx = (theme: Theme): SystemStyleObject => ({
  fontSize: bloxValues.tinyFontSizeRem,
  color: theme.palette.text.secondary,
  alignSelf: 'flex-end',
});

export const activeFiltersBoxSx: SxProps<Theme> = {
  pt: 0,
  pb: 0.5,
  pl: 1,
  pr: 1,
  gap: 0.5,
  display: 'flex',
  flexWrap: {
    xs: 'nowrap', // horiz scroll on small screens
    sm: 'wrap',
  },
};
